import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import WarningIcon from 'assets/LeadInfo/warningGrey.svg';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import TextArea from 'common-ui/textArea';
import { createOrderOffer, updateOrderOffer } from 'features/newsales/newsalesActions';

import {
  ButtonRow,
  CancelButton,
  ErrorMessage,
  Label,
  NoteIcon,
  NoteSection,
  PickerContainer,
  PickerRow,
  SubmitButton,
} from './CreateOrderModal.styles';

const CreateOrderModal = ({
  isOpen,
  onClose,
  offer,
  leadId,
  onSuccess,
  orderOffer = null,
}) => {
  const dispatch = useDispatch();
  const [count, setCount] = useState('');
  const [date, setDate] = useState(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isEditMode = Boolean(orderOffer);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && orderOffer) {
        setCount(orderOffer.count || '');
        setDate(orderOffer.preferredDate ? new Date(orderOffer.preferredDate) : null);
        setDescription(orderOffer.description || '');
      } else {
        setCount('');
        setDate(null);
        setDescription('');
      }
      setError('');
      setIsSubmitting(false);
    }
  }, [isOpen, isEditMode, orderOffer]);

  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async () => {
    setError('');

    if (!count || count <= 0) {
      setError('Please enter a valid count');
      return;
    }

    if (!isEditMode && !offer?.id) {
      setError('No offer selected');
      return;
    }

    setIsSubmitting(true);

    try {
      const orderData = {
        count: String(count),
      };

      if (date) {
        orderData.preferredDate = formatDateForAPI(date);
      }

      if (description && description.trim()) {
        orderData.description = description.trim();
      }

      if (isEditMode) {
        await dispatch(
          updateOrderOffer({
            leadId,
            orderOfferId: orderOffer.id,
            orderData,
          })
        ).unwrap();
      } else {
        orderData.offerId = offer.id;
        await dispatch(
          createOrderOffer({
            leadId,
            orderData,
          })
        ).unwrap();
      }

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (err) {
      setError(err?.message || err || `Failed to ${isEditMode ? 'update' : 'create'} order`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Order' : 'Add an Order'}
      footer={false}
      width="450px"
    >
      <PickerRow>
        <PickerContainer>
          <Label>Count*</Label>
          <Input
            placeholder="Count"
            type="number"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            height="48px"
            min="1"
            disabled={isSubmitting}
          />
        </PickerContainer>

        <PickerContainer>
          <Label className="margin">
            Preferred Date
            <NoteIcon src={WarningIcon} />
          </Label>
          <CustomDatePicker
            value={date}
            onChange={setDate}
            placeholder="dd/mm/yy"
            height="48px"
            disabled={isSubmitting}
          />
        </PickerContainer>
      </PickerRow>

      <NoteSection>
        <Label>
          Description
          <NoteIcon src={WarningIcon} />
        </Label>
        <TextArea
          placeholder="Describe offer"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={250}
          resizeVertical={false}
          resizeHorizontal={false}
          disabled={isSubmitting}
        />
      </NoteSection>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonRow>
        <CancelButton onClick={onClose} disabled={isSubmitting}>
          Cancel
        </CancelButton>
        <SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting
            ? isEditMode
              ? 'Updating...'
              : 'Creating...'
            : isEditMode
              ? 'Update'
              : 'Create'}
        </SubmitButton>
      </ButtonRow>
    </Modal>
  );
};

export default CreateOrderModal;
